<template>
  <div>
    <div v-if="!editTimes" class="relative mt-4 space-y-4">
      <div class="grid gap-8 md:grid-cols-12 md:items-end">
        <LabeledText class="md:col-span-5" :label="t('startTime')">{{
          formatDate(transfer.startDate)
        }}</LabeledText>
        <div
          class="self-center max-sm:w-6 max-sm:justify-self-center md:col-span-2 md:max-w-10 md:pb-3"
        >
          <HorizontalArrow left right class="max-sm:rotate-90" />
        </div>
        <LabeledText class="md:col-span-5" :label="t('arrivalTime')">{{
          formatDate(transfer.endDate)
        }}</LabeledText>
      </div>
      <div class="grid gap-8 md:grid-cols-12 md:items-end">
        <LabeledText class="md:col-span-5" :label="t('requiredTravelTime')">{{
          durations.requiredTravelTime
        }}</LabeledText>
        <div class="max-md:hidden md:col-span-2 md:max-w-10" aria-hidden="true" />
        <LabeledText class="md:col-span-5" :label="t('buffer')">{{ durations.buffer }}</LabeledText>
      </div>
      <CVButton
        size="icon"
        variant="ghost"
        class="absolute right-0 top-0"
        data-testid="edit-transfer-times-button"
        @click="editTimes = true"
      >
        <PencilIcon class="size-4" />
      </CVButton>
    </div>
    <TransferTimesForm
      v-else
      :transfer="transfer"
      :booking-allocation="bookingAllocation"
      @saved="editTimes = false"
      @cancelled="editTimes = false"
    />
  </div>
</template>

<script lang="ts" setup>
import type { Allocation } from '@/entities/allocations/allocation.entity';
import TransferTimesForm from './TransferTimesForm.vue';
import { computed, ref } from 'vue';
import HorizontalArrow from '@/components/HorizontalArrow.vue';
import LabeledText from '@/components/LabeledText.vue';
import { useI18n } from 'vue-i18n';
import { formatDate } from '@/hooks/use-formatted-date';
import CVButton from '@/components/buttons/CVButton.vue';
import { PencilIcon } from 'lucide-vue-next';
import { formatDurationMinutes } from '@/utils/duration-input';

const { t } = useI18n();

const editTimes = ref(false);

const props = defineProps<{
  transfer: Allocation;
  bookingAllocation: Allocation;
}>();

const durations = computed(() => {
  const bufferMinutes = props.transfer.bufferMinutes ?? 0;
  const calculatedMinutes = props.transfer.calculatedTransferMinutes ?? 0;
  const effectiveTravelMinutes = props.transfer.customTransferMinutes ?? calculatedMinutes;
  const formattedTravel = formatDurationMinutes(effectiveTravelMinutes);
  const isTravelOverridden = props.transfer.customTransferMinutes != null;

  return {
    requiredTravelTime: isTravelOverridden
      ? t('travelOverrideDisplay', {
          effective: formattedTravel,
          calculated: formatDurationMinutes(calculatedMinutes),
        })
      : formattedTravel,
    buffer: formatDurationMinutes(bufferMinutes),
  };
});
</script>

<i18n lang="json">
{
  "en": {
    "requiredTravelTime": "Required Travel Time",
    "buffer": "Buffer",
    "startTime": "Start Time",
    "arrivalTime": "Arrival Time",
    "editTimes": "Edit Times",
    "travelOverrideDisplay": "{effective} (custom, calculated: {calculated})"
  },
  "de": {
    "requiredTravelTime": "Benötigte Fahrtdauer",
    "buffer": "Puffer",
    "startTime": "Startzeit",
    "arrivalTime": "Ankunftszeit",
    "editTimes": "Zeiten bearbeiten",
    "travelOverrideDisplay": "{effective} (individuell, berechnet: {calculated})"
  },
  "es": {
    "requiredTravelTime": "Tiempo de trayecto necesario",
    "buffer": "Margen",
    "startTime": "Hora de inicio",
    "arrivalTime": "Hora de llegada",
    "editTimes": "Editar horarios",
    "travelOverrideDisplay": "{effective} (personalizado, calculado: {calculated})"
  }
}
</i18n>
